import useSelectedAccountId from "components/common/hooks/useSelectedAccountId";
import i18next from "i18next";
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { isAbortError } from "services/common/ApiUtils";
import { hidePieSliceLabel } from "services/common/ChartUtils";
import { ERROR, ThirdpartyScoresFieldsNames } from "services/common/Constants";
import { getEsgCount } from "services/edge/DataBlocksService";
import { Context } from "states/Store";
import useStyles from "style/js-style/containers/dashboard/TabStyle";
import * as am5 from "@amcharts/amcharts5";
import { theme } from "style/globalStyle";
import { Grid, Menu, MenuItem, Paper, Typography } from "@mui/material";
import RiskScorePieChart from "../RiskScorePieChart";
import { formatNumber } from "services/common/Utils";
import RiskSubScoresPieChart from "../RiskSubScoresPieChart";
import { getEsgDepths } from 'services/common/DataBlocksUtils';


const TabDashboardEsg = () => {
    const { classes } = useStyles();
    const [state, dispatch] = useContext(Context);

    const accountId = useSelectedAccountId();

    const [esgScoreCount, setEsgScoreCount] = useState();
    const [esgDepthCount, setEsgDepthCount] = useState();
    const [esgRiskEnvironmentCount, setEsgRiskEnvironmentCount] = useState();
    const [esgRiskEnvironmentDepthCount, setEsgRiskEnvironmentDepthCount] = useState();
    const [esgRiskSocialCount, setEsgRiskSocialCount] = useState();
    const [esgRiskSocialDepthCount, setEsgRiskSocialDepthCount] = useState();
    const [esgRiskGovernanceCount, setEsgRiskGovernanceCount] = useState(); 
    const [esgRiskGovernanceDepthCount, setEsgRiskGovernanceDepthCount] = useState();
   

    const abortCtrlRef = useRef();

    const severityPieDataScore = useMemo(() => ({
        "1": {
          color: theme.palette.primary.primary400,
          order: 1,
        },
        "2": {
          color: theme.palette.primary.primary600,
          order: 2,
        },
        "3": {
          color: theme.palette.warning.warning400,
          order: 3,
        },
        "4": {
          color: theme.palette.warning.warning600,
          order: 4,
        },
        "5": {
          color: theme.palette.error.error400,
          order: 5,
        },
        "0": {
          color: theme.palette.grey.grey700,
          order: 6,
        },
        "-1": {
          color: theme.palette.grey.grey400,
          order: 7,
        },
      }), []);

      const severityPieDataDepth = useMemo(() => ({
        "A": {
          color: theme.palette.primary.primary600,
          order: 1,
        },
        "B": {
          color: theme.palette.primary.primary500,
          order: 2,
        },
        "C": {
          color: theme.palette.primary.primary400,
          order: 3,
        },
        "D": {
          color: theme.palette.warning.warning400,
          order: 4,
        },
        "E": {
          color: theme.palette.warning.warning500,
          order: 5,
        },
        "F": {
          color: theme.palette.warning.warning600,
          order: 6,
        },
        "G": {
          color: theme.palette.error.error400,
          order: 7,
        },
        "H": {
          color: theme.palette.error.error500,
          order: 8,
        },
        "I": {
          color: theme.palette.error.error600,
          order: 9,
        },
        "0": {
          color: theme.palette.grey.grey700,
          order: 10,
        },
        "-1": {
          color: theme.palette.grey.grey400,
          order: 11,
        },
      }), []);


    const mapResultsCount = (results, severityPieData, isDepthScore, typeFilter) => {
        const total = results.scoreCounts.map((d) => d.count).reduce((partialSum, a) => partialSum + a, 0);
        return {
            data: results.scoreCounts
                .map((item) => ({
                    id: item.scoreLabel,
                    label: item.scoreLabel,
                    itemLabel:
                        item.scoreValue === "0" ? i18next.t("ESG.scoreUnknownLabel") :  
                        item.scoreValue === "-1" ? i18next.t("ESG.noEsgScoring") :
                        isDepthScore ? getEsgDepths().find(indice => indice.value === item.scoreValue)?.shortDescription
                        : i18next.t(`ESG.score${item.scoreLabel}label`),
                    type: typeFilter,
                    value: item.count,
                    formattedValue: formatNumber(item.count),
                    color: severityPieData[item.scoreLabel].color,
                    settings: {
                        fill: am5.color(
                        severityPieData[item.scoreLabel].color
                        ),
                    },
                    labelSettings: {
                        forceHidden: hidePieSliceLabel(item.count, total),
                    },
                }))
                .sort((a, b) =>
                    severityPieData[a.label].order > severityPieData[b.label].order
                        ? 1
                        : -1
                    ),
          total: formatNumber(total)
        }
    }

  const fetchEsgCounts = useCallback(
    (queryfilter) => {
      
      if(abortCtrlRef.current) {
        abortCtrlRef.current.abort();
      }
  
      abortCtrlRef.current = new AbortController();
      const signal = abortCtrlRef.current.signal;

      if (accountId && state.account) {
        (async () => {
          try {
            const res = await getEsgCount(
              accountId,
              queryfilter,
              null,
              signal
            );

            const result = res.content;

            if(result) {
                setEsgScoreCount(mapResultsCount(result.find(item => item.score === "esgScore"), severityPieDataScore, false, ThirdpartyScoresFieldsNames.ESG_SCORE));
                setEsgDepthCount(mapResultsCount(result.find(item => item.score === "esgDepth"), severityPieDataDepth, true, ThirdpartyScoresFieldsNames.ESG_DEPTH));
                setEsgRiskEnvironmentCount(mapResultsCount(result.find(item => item.score === "esgRiskEnvironment"), severityPieDataScore, false, ThirdpartyScoresFieldsNames.ESG_RISK_ENVIRONMENTAL));
                setEsgRiskEnvironmentDepthCount(mapResultsCount(result.find(item => item.score === "esgRiskEnvironmentDepth"), severityPieDataDepth, true, ThirdpartyScoresFieldsNames.ESG_DEPTH));
                setEsgRiskSocialCount(mapResultsCount(result.find(item => item.score === "esgRiskSocial"), severityPieDataScore, false, ThirdpartyScoresFieldsNames.ESG_RISK_SOCIAL));
                setEsgRiskSocialDepthCount(mapResultsCount(result.find(item => item.score === "esgRiskSocialDepth"), severityPieDataDepth, true, ThirdpartyScoresFieldsNames.ESG_DEPTH));
                setEsgRiskGovernanceCount(mapResultsCount(result.find(item => item.score === "esgRiskGovernance"), severityPieDataScore, false, ThirdpartyScoresFieldsNames.ESG_RISK_GOVERNANCE));
                setEsgRiskGovernanceDepthCount(mapResultsCount(result.find(item => item.score === "esgRiskGovernanceDepth"), severityPieDataDepth, true, ThirdpartyScoresFieldsNames.ESG_DEPTH));
            }
          } catch (e) {
            if(!isAbortError(e)) {
              dispatch({ type: "ALERT", alert: { type: ERROR, msg: e.message } });
            } 
          }
        })();
      }
    },
    [accountId, dispatch, severityPieDataDepth, severityPieDataScore, state.account]
  );

  // Manage call counts and query filter
  useEffect(() => {
    // add queryJob
    let queryObj = {};
    if (state.graphFilters?.pie?.length > 0) {
      queryObj = state.graphFilters.pie.reduce((result, item) => {
        const { id, type } = item;
        if (!result[type]) {
          result[type] = [];
        }
        result[type].push(id);
        return result;
      }, {});
    }

    fetchEsgCounts(queryObj);
    return () => {
      abortCtrlRef.current?.abort();
    }
  }, [fetchEsgCounts, state.graphFilters.pie]);


  // Manage swith between score or deph graph : 
  const [openSelectTypeScore, setOpenSelectTypeScore] = useState(null);
  const [typeScoreSelected, setTypeScoreSelected] = useState("score");

  const changeTypeScoreHandler  = (type) => {
    setTypeScoreSelected(type);
    setOpenSelectTypeScore(null)
  }

  return (
    <Paper elevation={0} className={classes.paperContainer}>
      <Grid container spacing={2} style={{height: "100%"}}>
        <Grid item xs={4} lg={4} key="ESG-global">
          <Paper className={classes.graphPaper}>
            <Typography variant="h3" className={classes.graphAreaTitle}>
              <div style={{display: "flex"}}>
                <span>{typeScoreSelected === "score" ?i18next.t(`ESG.dashboardScoreCardTitle`) :  i18next.t(`ESG.dashboardDepthTitle`)}</span>
                {/* La ligne ci-dessous permet d'ajouter un boutton donnant la possibilité de switcher vers le dashboard des indices de fiabilité. Désactivé pour le moment */}
               {/*  <span className={classes.expandIconButton} onClick={(event) => setOpenSelectTypeScore(event.currentTarget)}><EAIcon icon="expand_more"/></span> */}
                <Menu open={Boolean(openSelectTypeScore)} onClose={() => setOpenSelectTypeScore(null)} anchorEl={openSelectTypeScore}>
                  <MenuItem onClick={() => changeTypeScoreHandler("score")} disabled={typeScoreSelected === "score"}>
                    {i18next.t(`ESG.dashboardScoreCardTitle`)}
                  </MenuItem>
                  <MenuItem onClick={() => changeTypeScoreHandler("depth")} disabled={typeScoreSelected === "depth"}>
                    {i18next.t(`ESG.dashboardDepthTitle`)}
                  </MenuItem>
                </Menu>
              </div>
            </Typography>
              <RiskScorePieChart subTab={"ESG"} scoreCount={typeScoreSelected === "score" ? esgScoreCount : esgDepthCount}
               legendHeightPercent={25} legendTitle={i18next.t("ESG.dashboardLegendTitle")} />
          </Paper>
        </Grid>
        <Grid item xs={8} lg={8} key="ESG-subscores">
            <RiskSubScoresPieChart title={i18next.t(`ESG.dashboardSubScoresTitle`)} riskLabel="ESG" subScores={[
                {scoreLabel: i18next.t(`ESG.labelEnvironment`), data: typeScoreSelected === "score" ? esgRiskEnvironmentCount : esgRiskEnvironmentDepthCount}, 
                {scoreLabel: i18next.t(`ESG.labelSocial`), data: typeScoreSelected === "score" ? esgRiskSocialCount : esgRiskSocialDepthCount}, 
                {scoreLabel: i18next.t(`ESG.labelGovernance`), data: typeScoreSelected === "score" ? esgRiskGovernanceCount : esgRiskGovernanceDepthCount}
            ]} />
        </Grid>
      </Grid>
    </Paper>
  );

}

export default TabDashboardEsg;